import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Phone, MessageCircle, ArrowRight, DollarSign, Clock, Baby, Plane, MapPin, ShoppingBag, Ship, Building2, Car } from 'lucide-react';
import PageSEO from '@/components/PageSEO';
import { BUSINESS, IMAGES } from '@/lib/constants';
import { webPage, service, breadcrumb } from '@/lib/schemas';

const HERO_IMG = 'https://images.unsplash.com/photo-1770938474431-f83143a10268?fm=jpg&q=85&w=1400&auto=format&fit=crop';

const ACTIVE_AREAS = [
  'New Dorp', 'Grant City', 'Dongan Hills', 'Oakwood',
  'Richmondtown', 'Todt Hill', 'Arrochar', 'Shore Acres',
];

const OTHER_AREAS = [
  { region: 'North Shore', neighborhoods: 'St. George, Rosebank, New Brighton, Port Richmond, and nearby areas' },
  { region: 'West / Central', neighborhoods: 'New Springville, Bulls Head, Mariners Harbor, and nearby areas' },
  { region: 'South Shore', neighborhoods: 'Great Kills, Eltingville, Annadale, Huguenot, Tottenville, and nearby areas' },
];

const DESTINATIONS = [
  { label: 'St. George Ferry', icon: Ship, to: '/local-rides-staten-island' },
  { label: 'Staten Island Mall', icon: ShoppingBag, to: '/request-a-ride' },
  { label: 'Newark Airport', icon: Plane, to: '/airport-rides-staten-island' },
  { label: 'JFK Airport', icon: Plane, to: '/airport-rides-staten-island' },
  { label: 'LaGuardia Airport', icon: Plane, to: '/airport-rides-staten-island' },
  { label: 'Brooklyn', icon: Building2, to: '/request-a-ride' },
  { label: 'Manhattan', icon: Building2, to: '/request-a-ride' },
  { label: 'New Jersey', icon: MapPin, to: '/request-a-ride' },
];

const FAQS = [
  {
    q: 'What areas are you most active in?',
    a: 'Island Ride is especially active around central and east-side Staten Island neighborhoods, including New Dorp, Grant City, Dongan Hills, Oakwood, Richmondtown, Todt Hill, Arrochar, and Shore Acres.',
  },
  {
    q: 'Do you serve all of Staten Island?',
    a: 'Yes. Island Ride accepts ride requests across Staten Island. Availability may depend on timing, pickup location, destination, and current driver positioning. Call or WhatsApp us and we\'ll do our best to help.',
  },
  {
    q: 'What are your hours?',
    a: 'Island Ride is available 24/7, every day of the week.',
  },
  {
    q: 'Do you go outside Staten Island?',
    a: 'Yes. Island Ride provides airport rides and out-of-town trips to nearby destinations including Brooklyn, Manhattan, New Jersey, Newark Airport, JFK, and LaGuardia.',
    hasAirportLink: true,
  },
  {
    q: 'How do I book a ride?',
    a: 'Call, WhatsApp, or submit a ride request online. Ride requests are confirmed after Island Ride reviews timing, availability, and fare.',
  },
];

const SERVICE_AREA_SCHEMAS = [
  webPage('Staten Island Service Area', 'Island Ride serves local rides, airport trips, and out-of-town travel across Staten Island, with especially active service around central and east-side neighborhoods.', '/staten-island-service-area'),
  { '@context': 'https://schema.org', '@type': 'Service', '@id': 'https://goislandride.com/staten-island-service-area/#service', name: 'Staten Island Cab Service Area', serviceType: 'Cab service area', description: 'Island Ride serves local rides, airport trips, and out-of-town travel across Staten Island, with especially active service around central and east-side neighborhoods.', provider: { '@id': 'https://goislandride.com/#business' }, areaServed: [
    { '@type': 'Place', name: 'Staten Island' },
    { '@type': 'Place', name: 'New Dorp' },
    { '@type': 'Place', name: 'Grant City' },
    { '@type': 'Place', name: 'Dongan Hills' },
    { '@type': 'Place', name: 'Oakwood' },
    { '@type': 'Place', name: 'Richmondtown' },
    { '@type': 'Place', name: 'Todt Hill' },
    { '@type': 'Place', name: 'Arrochar' },
    { '@type': 'Place', name: 'Shore Acres' },
  ], url: 'https://goislandride.com/staten-island-service-area/' },
  breadcrumb('Service Area', '/staten-island-service-area'),
];

export default function ServiceArea() {
  return (
    <>
      <PageSEO
        title="Staten Island Service Area — Neighborhoods We Serve | Island Ride"
        description="Island Ride serves all of Staten Island — from St. George to Tottenville. Local rides, airport trips, and out-of-town travel. See our full service area."
        path="/staten-island-service-area"
        schemas={SERVICE_AREA_SCHEMAS}
      />
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="service-area-hero">
        <img
          src={HERO_IMG}
          alt="Aerial view of a tree-lined suburban neighborhood in Staten Island — Island Ride local taxi and car service covering New Dorp, Grant City, Dongan Hills, and surrounding areas"
          title="Staten Island service area — Island Ride cab service"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-background/60 to-background/20" />
        <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 py-16 sm:py-20">
          <h1
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary"
            data-testid="service-area-h1"
          >
            Staten Island Service Area
          </h1>
          <p className="mt-4 text-muted-foreground text-base sm:text-lg max-w-lg leading-relaxed">
            Local rides, airport trips, and out-of-town travel across Staten Island. Especially active around the island's central and east-side neighborhoods.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3 text-sm font-medium text-primary">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/90 px-3 py-1">
              <DollarSign className="h-3.5 w-3.5" /> Starting at $12
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/90 px-3 py-1">
              <Car className="h-3.5 w-3.5" /> Sedans · Minivans · SUVs
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/90 px-3 py-1">
              <Baby className="h-3.5 w-3.5" /> Pre-installed child seats
            </span>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild data-testid="service-area-cta-request">
              <Link to="/request-a-ride">Request a ride<ArrowRight className="h-4 w-4 ml-1.5" /></Link>
            </Button>
            <Button asChild variant="outline" className="bg-background/80" data-testid="service-area-cta-call">
              <a href={BUSINESS.phoneTel}><Phone className="h-4 w-4" /><span className="ml-1.5">{BUSINESS.phone}</span></a>
            </Button>
            <Button asChild variant="outline" className="bg-background/80" data-testid="service-area-cta-whatsapp">
              <a href={BUSINESS.whatsappUrl} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" /><span className="ml-1.5">WhatsApp</span></a>
            </Button>
          </div>
        </div>
      </section>

      {/* Map + Especially Active Areas */}
      <section className="bg-secondary border-y" data-testid="service-area-active">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-10 sm:py-14">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2
                className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary"
                data-testid="active-areas-heading"
              >
                Where we're most active
              </h2>
              <p className="mt-3 text-muted-foreground max-w-xl">
                Our busiest pickup areas right now include:
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {ACTIVE_AREAS.map((name) => (
                  <span
                    key={name}
                    className="rounded-full bg-background border px-3.5 py-1.5 text-sm font-medium text-foreground"
                    data-testid={`active-area-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {name}
                  </span>
                ))}
              </div>
              <p className="mt-5 text-sm text-muted-foreground">
                Rides available across all of Staten Island by request. Looking for <Link to="/local-rides-staten-island" className="text-primary font-medium hover:underline">local rides</Link> or have questions? <Link to="/contact" className="text-primary font-medium hover:underline">Contact us</Link>.
              </p>
            </div>
            <div className="flex justify-center">
              <div className="w-full max-w-md rounded-2xl overflow-hidden bg-background border shadow-[var(--shadow-card)]">
                <img
                  src={IMAGES.serviceAreaMap}
                  alt="Island Ride service area map showing especially active coverage around central and eastern Staten Island neighborhoods including New Dorp, Grant City, Dongan Hills, Oakwood, and Richmondtown"
                  className="block w-full h-auto"
                  loading="lazy"
                  data-testid="service-area-map-img"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Areas by Request */}
      <section className="section-pad" data-testid="service-area-other">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <h2
            className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary"
            data-testid="other-areas-heading"
          >
            Available across Staten Island by request
          </h2>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            Island Ride accepts ride requests across Staten Island when availability allows. If you're outside our especially active area, call or WhatsApp us — we'll do our best to help.
          </p>
          <div className="mt-8 grid sm:grid-cols-3 gap-6">
            {OTHER_AREAS.map((area) => (
              <div key={area.region} data-testid={`other-area-${area.region.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <h3 className="font-display text-sm font-bold text-foreground">{area.region}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{area.neighborhoods}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Destinations */}
      <section
        className="section-pad"
        style={{ background: 'linear-gradient(to bottom, rgba(16,97,95,0.06), rgba(16,97,95,0))' }}
        data-testid="popular-destinations"
      >
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <h2
            className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary"
            data-testid="destinations-heading"
          >
            Popular destinations
          </h2>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {DESTINATIONS.map((d) => (
              <Link
                key={d.label}
                to={d.to}
                className="flex items-center gap-3 rounded-xl bg-background border p-4 transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-md"
                data-testid={`dest-${d.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-primary shrink-0">
                  <d.icon className="h-4 w-4" />
                </div>
                <span className="font-display text-sm font-semibold leading-tight">{d.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="section-pad"
        style={{ background: 'linear-gradient(to right, rgba(16,97,95,0.06), rgba(16,97,95,0))' }}
        data-testid="service-area-faq-section"
      >
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <h2
            className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary"
            data-testid="service-area-faq-heading"
          >
            Frequently asked questions
          </h2>
          <div className="mt-8 max-w-3xl">
            <Accordion type="single" collapsible className="space-y-2" data-testid="service-area-faq">
              {FAQS.map((faq) => (
                <AccordionItem key={faq.q} value={faq.q} className="rounded-lg border bg-background px-5">
                  <AccordionTrigger
                    className="text-sm font-semibold text-left py-4 hover:no-underline"
                    data-testid={`service-faq-trigger-${faq.q.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground pb-4 leading-relaxed">
                    {faq.hasAirportLink ? (
                      <>
                        Yes. Island Ride provides{' '}
                        <Link to="/airport-rides-staten-island" className="text-primary font-medium hover:underline">airport rides</Link>
                        {' '}and out-of-town trips to nearby destinations including Brooklyn, Manhattan, New Jersey, Newark Airport, JFK, and LaGuardia.
                      </>
                    ) : (
                      faq.a
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Bottom CTA Strip */}
      <section className="bg-primary text-primary-foreground" data-testid="service-area-bottom-cta">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-3.5 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-y-3 gap-x-5 text-center">
            <span className="font-display font-semibold text-sm sm:text-[15px]">Need a ride?</span>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button asChild size="sm" variant="secondary" className="h-8 text-xs" data-testid="sa-bottom-cta-call">
                <a href={BUSINESS.phoneTel}><Phone className="h-3.5 w-3.5" /><span className="ml-1.5">Call {BUSINESS.phone}</span></a>
              </Button>
              <Button asChild size="sm" variant="secondary" className="h-8 text-xs" data-testid="sa-bottom-cta-whatsapp">
                <a href={BUSINESS.whatsappUrl} target="_blank" rel="noreferrer"><MessageCircle className="h-3.5 w-3.5" /><span className="ml-1.5">WhatsApp</span></a>
              </Button>
              <Button asChild size="sm" variant="secondary" className="h-8 text-xs" data-testid="sa-bottom-cta-request">
                <Link to="/request-a-ride">Request a ride<ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
